import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  Link,
  Progress,
  Radio,
  RadioGroup,
  Select,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable, FormField, PageContainer } from '@/design-system/components';
import type { QrJobItem } from '@/lib/sdk/qr-jobs/types';
import type { MasterDataRow } from '@/lib/sdk/master-data/types';
import { useBulkQrJob } from '../hooks';

const statusColorMap: Record<string, string> = {
  pending: 'gray', done: 'green', failed: 'red',
};

const columns: ColumnDef<QrJobItem, unknown>[] = [
  { header: '#', accessorKey: 'seq', size: 60 },
  { header: 'Key', accessorKey: 'key', size: 140 },
  { header: 'Label', accessorKey: 'label', size: 200 },
  {
    header: 'Status', accessorKey: 'status', size: 100,
    cell: (info) => {
      const status = info.getValue() as string;
      return <Badge colorScheme={statusColorMap[status] ?? 'gray'} textTransform="capitalize">{status}</Badge>;
    },
  },
  {
    header: 'S3 URL', accessorKey: 'image_url', size: 320,
    cell: (info) => {
      const imageUrl = info.getValue() as string | null | undefined;
      if (!imageUrl) return <Text color="text-secondary">—</Text>;
      return (
        <HStack spacing={3}>
          <Image src={imageUrl} alt={info.row.original.file_stem} boxSize="40px" />
          <Link href={imageUrl} isExternal fontSize="xs" noOfLines={1} maxW="260px">{imageUrl}</Link>
        </HStack>
      );
    },
  },
  {
    header: 'Error', accessorKey: 'error', size: 220,
    cell: (info) => {
      const error = info.getValue() as string | null | undefined;
      if (!error) return <Text color="text-secondary">—</Text>;
      return <Text fontSize="xs" color="red.500" noOfLines={2} maxW="220px">{error}</Text>;
    },
  },
];

const recordColumns: ColumnDef<MasterDataRow, unknown>[] = [
  { header: 'Key', accessorKey: 'key', size: 160 },
  { header: 'Label', accessorKey: 'label', size: 260 },
  {
    header: 'Added', accessorKey: 'created_at', size: 180,
    cell: (info) => <Text fontSize="sm" whiteSpace="nowrap">{new Date(info.getValue() as string).toLocaleString()}</Text>,
  },
];

const SEARCH_DEBOUNCE_MS = 300;

export function BulkQrCodesPage() {
  const p = useBulkQrJob();
  const { register, formState: { errors } } = p.form;
  const remainingAttributes = p.attributeOptions.filter((option) => !p.attributeSources.includes(option.value));
  const datasetField = register('dataset');
  const [searchText, setSearchText] = useState(p.recordSearch);
  const { recordSearch, onRecordSearch } = p;
  useEffect(() => {
    // Only apply real edits; re-applying the current search would reset the record page.
    if (searchText === recordSearch) return undefined;
    const timer = setTimeout(() => onRecordSearch(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText, recordSearch, onRecordSearch]);

  return (
    <PageContainer title="Bulk QR Codes">
      <VStack spacing={4} align="stretch">
        <Box
          as="form"
          onSubmit={p.onGenerate}
          p={4} borderWidth="1px" borderColor="border-default" borderRadius="lg" bg="bg-surface"
        >
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} align="flex-start" flexWrap="wrap">
              <Box minW="260px" flex="1">
                <FormField label="Chatbot" name="chatbot_id" type="select" register={register} required
                  error={errors.chatbot_id} testId="qrb-chatbot"
                  options={[{ value: '', label: 'Select a chatbot' }, ...p.chatbotOptions]} />
              </Box>
              <Box minW="220px" flex="1">
                <FormControl isRequired isInvalid={!!errors.dataset}>
                  <FormLabel>Dataset</FormLabel>
                  <Select
                    data-testid="qrb-dataset"
                    {...datasetField}
                    onChange={(event) => {
                      void datasetField.onChange(event);
                      p.onDatasetChange();
                    }}
                  >
                    <option value="">Select a dataset</option>
                    {p.datasetOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </Select>
                  <FormErrorMessage>{errors.dataset?.message}</FormErrorMessage>
                </FormControl>
              </Box>
            </HStack>

            <FormControl isRequired isInvalid={!!errors.attribute_sources}>
              <FormLabel>Attributes (in slot order)</FormLabel>
              <Wrap spacing={2} mb={2} data-testid="qrb-attributes">
                {p.attributeSources.map((source, index) => (
                  <WrapItem key={source}>
                    <Tag size="md" colorScheme="brand" borderRadius="full">
                      <TagLabel>{index + 1}: {p.labelFor(source)}</TagLabel>
                      <TagCloseButton onClick={() => p.removeSource(source)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
              <Select
                maxW="320px"
                value=""
                // An adder, not the field's value (that's the tags above); a native `required`
                // here would block submit since its value is always empty.
                isRequired={false}
                isDisabled={!p.dataset || remainingAttributes.length === 0}
                onChange={(event) => p.addSource(event.target.value)}
                data-testid="qrb-add-attribute"
              >
                <option value="">Add attribute…</option>
                {remainingAttributes.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Select>
              <FormErrorMessage>{errors.attribute_sources?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.keys}>
              <FormLabel>Records</FormLabel>
              <RadioGroup
                value={p.scope}
                onChange={(value) => p.form.setValue('scope', value as 'all' | 'selected', { shouldValidate: p.form.formState.isSubmitted })}
                isDisabled={!p.dataset}
              >
                <HStack spacing={6}>
                  <Radio value="all" data-testid="qrb-scope-all">All records</Radio>
                  <Radio value="selected" data-testid="qrb-scope-selected">Selected records</Radio>
                </HStack>
              </RadioGroup>

              {p.scope === 'selected' && p.dataset && (
                <VStack align="stretch" spacing={3} mt={3}>
                  <HStack spacing={3} flexWrap="wrap" align="flex-end">
                    <Box minW="220px" flex="1">
                      <Text fontSize="sm" mb={1}>Search</Text>
                      <Input size="sm" placeholder="Key, label or field value" value={searchText}
                        onChange={(event) => setSearchText(event.target.value)} data-testid="qrb-record-search" />
                    </Box>
                    <Box minW="180px">
                      <Text fontSize="sm" mb={1}>Added since</Text>
                      <Input size="sm" type="date" value={p.recordsAddedSince}
                        onChange={(event) => p.onRecordsAddedSince(event.target.value)} data-testid="qrb-record-since" />
                    </Box>
                  </HStack>
                  <HStack justify="space-between" flexWrap="wrap">
                    <HStack spacing={3}>
                      <Text fontSize="sm" data-testid="qrb-selected-count">
                        {p.selectedKeys.length} selected
                      </Text>
                      <Button size="xs" variant="ghost" onClick={() => p.setKeys([])}
                        isDisabled={p.selectedKeys.length === 0}>Clear</Button>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="text-secondary">
                        {p.recordTotal} record{p.recordTotal !== 1 ? 's' : ''} · Page {p.recordPage} of {p.recordPageCount}
                      </Text>
                      <Button size="xs" variant="outline" onClick={() => p.setRecordPage((prev) => Math.max(1, prev - 1))}
                        isDisabled={p.recordPage <= 1}>‹</Button>
                      <Button size="xs" variant="outline" onClick={() => p.setRecordPage((prev) => Math.min(p.recordPageCount, prev + 1))}
                        isDisabled={p.recordPage >= p.recordPageCount}>›</Button>
                    </HStack>
                  </HStack>
                  <DataTable
                    data={p.records}
                    columns={recordColumns}
                    isLoading={p.isLoadingRecords}
                    emptyMessage="No records match."
                    enableSorting={false} enableFiltering={false} enablePagination={false}
                    enableSelection={true}
                    selectedRows={p.selectedKeys}
                    getRowId={(row) => row.key}
                    onRowSelectionChange={(event) => p.toggleKey(event.rowId, event.checked)}
                    onSelectAllChange={(event) => p.togglePageKeys(event.affectedRows.map((row) => row.key), event.checked)}
                    stickyHeader={true} maxTableHeight="360px"
                  />
                </VStack>
              )}
              <FormErrorMessage>{errors.keys?.message}</FormErrorMessage>
            </FormControl>

            <Box>
              <Text fontSize="sm" color="text-secondary">QR message</Text>
              <Text fontFamily="mono" fontSize="sm" data-testid="qrb-preview">{p.messagePreview}</Text>
              <Text fontSize="xs" color="text-secondary" mt={1}>
                Rows whose attribute values are empty or contain spaces are marked failed.
              </Text>
            </Box>

            {p.createError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {p.createError.message}
              </Alert>
            )}

            <HStack>
              <Button type="submit" colorScheme="brand" isLoading={p.isSubmitting}
                isDisabled={p.isRunning} data-testid="qrb-generate">
                {p.scope === 'selected'
                  ? `Generate ${p.selectedKeys.length} QR Code${p.selectedKeys.length !== 1 ? 's' : ''}`
                  : 'Generate QR Codes'}
              </Button>
            </HStack>
          </VStack>
        </Box>

        {p.job && (
          <>
            <HStack justify="space-between" flexWrap="wrap" spacing={4}>
              <VStack align="start" spacing={1} flex="1" minW="240px">
                <Text fontSize="sm" data-testid="qrb-progress">
                  {p.processed} of {p.job.total} processed · {p.job.succeeded} generated · {p.job.failed} failed
                </Text>
                <Progress value={p.job.total ? (p.processed / p.job.total) * 100 : 0} size="sm"
                  colorScheme={p.isCompleted ? 'green' : 'brand'} w="100%" borderRadius="full"
                  isAnimated={p.isRunning} hasStripe={p.isRunning} />
              </VStack>
              <Button as="a" href={p.isCompleted ? p.downloadUrl : undefined} download
                colorScheme="brand" variant="outline" isDisabled={!p.isCompleted} data-testid="qrb-download">
                Download All
              </Button>
            </HStack>

            <HStack justify="flex-end" spacing={2}>
              <Text fontSize="sm" color="text-secondary">Page {p.page} of {p.pageCount}</Text>
              <Button size="xs" variant="outline" onClick={() => p.setPage((prev) => Math.max(1, prev - 1))}
                isDisabled={p.page <= 1}>‹</Button>
              <Button size="xs" variant="outline" onClick={() => p.setPage((prev) => Math.min(p.pageCount, prev + 1))}
                isDisabled={p.page >= p.pageCount}>›</Button>
            </HStack>

            <DataTable
              data={p.items}
              columns={columns}
              isLoading={p.isLoadingItems}
              emptyMessage="No QR codes yet."
              enableSorting={false} enableFiltering={false} enablePagination={false}
              stickyHeader={true} maxTableHeight="600px"
              getRowId={(row) => String(row.seq)}
            />
          </>
        )}
      </VStack>
    </PageContainer>
  );
}
